import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { followupsApi } from '../lib/api';

// Stato di un follow-up: aperto o completato
export type FollowupStatus = 'pending' | 'done' | 'dismissed';

// Canali possibili per il ricontatto
export type FollowupChannel = 'email' | 'linkedin' | 'call' | 'other';

// Follow-up come restituito dall'API
export interface Followup {
  id: string;
  contact_id: string | null;
  due_at: string;
  note: string | null;
  channel: FollowupChannel | string;
  status: FollowupStatus | string;
  created_at?: string;
}

export function useFollowups(status?: string) {
  return useQuery({
    queryKey: ['followups', status ?? 'all'],
    queryFn: async () => {
      const res = await followupsApi.list(status);
      return (res?.items ?? res ?? []) as Followup[];
    },
  });
}

// Invalida sia l'elenco follow-up sia il dettaglio contatto (che li include)
function invalidateFollowups(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['followups'] });
  qc.invalidateQueries({ queryKey: ['contact'] });
}

export function useCreateFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { contact_id?: string; due_at: string; note: string; channel: string }) =>
      followupsApi.create(input),
    onSuccess: () => invalidateFollowups(qc),
  });
}

export function useUpdateFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status?: string; due_at?: string; note?: string } }) =>
      followupsApi.patch(id, body),
    onSuccess: () => invalidateFollowups(qc),
  });
}

export function useDeleteFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => followupsApi.remove(id),
    onSuccess: () => invalidateFollowups(qc),
  });
}
